import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Link } from "react-router-dom";
import { useUserAuth } from "../../../context/UserAuthContext";
import axios from "axios";

// Material UI Components
import {
    Grid,
    Card,
    CardContent,
    Typography,
    Box,
    CircularProgress,
    Button,
    Avatar,
    Badge,
    Chip,
    Container,
    Paper,
    CardActionArea,
    CardActions,
    alpha,
    Stack,
} from "@mui/material";
import {
    LocationCity as VenueIcon,
    Nature as LawnIcon,
    MeetingRoom as BanquetIcon,
    Pool as PoolIcon,
    Hotel as RoomIcon,
    Palette as DecorIcon,
    Add as AddIcon,
    Collections as CollectionsIcon,
    Image as ImageIcon,
    ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";

const VenuePreviousWork = () => {
    const [loading, setLoading] = useState(true);
    const { user, language, token } = useUserAuth();
    const merchant_id = user.uid;
    const base_url = import.meta.env.VITE_LOCAL_BASE_URL;

    // Tag definitions with icons and display names
    const tags = [
        {
            id: "venue_overview",
            name: language === "en" ? "Venue Overview" : "वेन्यू अवलोकन",
            icon: <VenueIcon sx={{ fontSize: 32 }} />,
            bgColor: "#f5f7fa",
        },
        {
            id: "lawn",
            name: language === "en" ? "Lawn" : "लॉन",
            icon: <LawnIcon sx={{ fontSize: 32 }} />,
            bgColor: "#f0f9f0",
        },
        {
            id: "banquet_hall",
            name: language === "en" ? "Banquet Hall" : "बैंकेट हॉल",
            icon: <BanquetIcon sx={{ fontSize: 32 }} />,
            bgColor: "#fff8f0",
        },
        {
            id: "swimming_pool",
            name: language === "en" ? "Swimming Pool" : "स्विमिंग पूल",
            icon: <PoolIcon sx={{ fontSize: 32 }} />,
            bgColor: "#f0f9fc",
        },
        {
            id: "rooms",
            name: language === "en" ? "Rooms" : "कमरे",
            icon: <RoomIcon sx={{ fontSize: 32 }} />,
            bgColor: "#f9f0fc",
        },
        {
            id: "decorations",
            name: language === "en" ? "Decorations" : "सजावट",
            icon: <DecorIcon sx={{ fontSize: 32 }} />,
            bgColor: "#fcf0f5",
        },
    ];

    const [imageCounts, setImageCounts] = useState({});

    // Fetch image counts for each tag
    const fetchImageCounts = async () => {
        try {
            setLoading(true);
            const counts = {};

            // Fetch all images first
            const res = await axios.get(
                `${base_url}/api/v1/previous-work/${merchant_id}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            // Group images by tag
            const imagesByTag = {};
            tags.forEach(tag => {
                imagesByTag[tag.id] = [];
            });

            if (res.data.data && Array.isArray(res.data.data)) {
                res.data.data.forEach(image => {
                    const tag = image.tag_name || "venue_overview";
                    if (imagesByTag[tag]) {
                        imagesByTag[tag].push(image);
                    }
                });
            }

            // Count images per tag
            tags.forEach(tag => {
                counts[tag.id] = imagesByTag[tag.id]?.length || 0;
            });

            setImageCounts(counts);
        } catch (error) {
            console.error("Error fetching image counts:", error);
            toast.error(language === "en" ? "Failed to load image counts" : "छवि गणना लोड करने में विफल");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!merchant_id) return;
        fetchImageCounts();
    }, [merchant_id, language]);

    return (
        <div className="page-wrapper">
            <div className="container-fluid">
                <div className="card p-5">
                    {/* <Container maxWidth="lg" sx={{ py: 3 }}> */}
                    <Box sx={{ mb: 4 }}>
                        <Typography
                            variant="h4"
                            sx={{
                                fontWeight: 700,
                                color: "text.primary",
                                mb: 1
                            }}
                        >
                            {language === "en" ? "Venue Gallery" : "वेन्यू गैलरी"}
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                color: "text.secondary",
                                maxWidth: 600
                            }}
                        >
                            {language === "en"
                                ? "Organize your venue photos into categories for better presentation"
                                : "बेहतर प्रस्तुति के लिए अपनी वेन्यू तस्वीरों को श्रेणियों में व्यवस्थित करें"}
                        </Typography>
                    </Box>
                    {loading ? (
                        <Grid container spacing={3}>
                            {[...Array(6)].map((_, index) => (
                                <Grid item xs={12} sm={6} md={4} key={index}>
                                    <Card sx={{ height: '100%' }}>
                                        <CardContent sx={{ p: 3 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                                                <CircularProgress size={40} />
                                            </Box>
                                            <Box sx={{ width: '60%', height: 24, bgcolor: 'grey.100', borderRadius: 1, mx: 'auto', mb: 1 }} />
                                            <Box sx={{ width: '40%', height: 20, bgcolor: 'grey.100', borderRadius: 1, mx: 'auto' }} />
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    ) : (
                        <Grid container spacing={3}>
                            {tags.map((tag) => {
                                const count = imageCounts[tag.id] || 0;
                                return (
                                    <Grid item xs={12} sm={6} md={4} key={tag.id}>
                                        <Card
                                            sx={{
                                                height: '100%',
                                                transition: 'all 0.2s ease-in-out',
                                                border: '1px solid',
                                                borderColor: 'divider',
                                                backgroundColor: 'background.paper',
                                                '&:hover': {
                                                    transform: 'translateY(-2px)',
                                                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                                                    borderColor: 'primary.main',
                                                },
                                            }}
                                        >
                                            <CardActionArea
                                                component={Link}
                                                to={`/dashboard/venue-previous-work/${tag.id}`}
                                                sx={{ height: '100%' }}
                                            >
                                                <CardContent sx={{ p: 3, height: '100%' }}>
                                                    <Stack spacing={2}>
                                                        {/* Icon and Count */}
                                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                            <Avatar
                                                                sx={{
                                                                    bgcolor: tag.bgColor,
                                                                    color: 'text.primary',
                                                                    width: 64,
                                                                    height: 64,
                                                                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                                                                }}
                                                            >
                                                                {tag.icon}
                                                            </Avatar>

                                                            <Badge
                                                                badgeContent={count}
                                                                color="success"
                                                                sx={{
                                                                    '& .MuiBadge-badge': {
                                                                        fontSize: '0.7rem',
                                                                        fontWeight: 600,
                                                                        height: 24,
                                                                        minWidth: 24,
                                                                        borderRadius: '12px',
                                                                    },
                                                                }}
                                                            />
                                                        </Box>

                                                        {/* Title */}
                                                        <Typography
                                                            variant="h6"
                                                            sx={{
                                                                fontWeight: 600,
                                                                color: 'text.primary',
                                                                lineHeight: 1.3,
                                                            }}
                                                        >
                                                            {tag.name}
                                                        </Typography>

                                                        {/* Description */}
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                color: 'text.secondary',
                                                                height: 40,
                                                                overflow: 'hidden',
                                                                display: '-webkit-box',
                                                                WebkitLineClamp: 2,
                                                                WebkitBoxOrient: 'vertical',
                                                            }}
                                                        >
                                                            {language === "en"
                                                                ? `Manage ${tag.name.toLowerCase()} photos`
                                                                : `${tag.name.toLowerCase()} तस्वीरें प्रबंधित करें`}
                                                        </Typography>

                                                        {/* Status and Action */}
                                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pt: 1 }}>
                                                            <Chip
                                                                icon={<ImageIcon />}
                                                                label={
                                                                    count > 0
                                                                        ? `${count} ${language === "en" ? "photos" : "तस्वीरें"}`
                                                                        : language === "en" ? "No photos" : "कोई तस्वीर नहीं"
                                                                }
                                                                size="small"
                                                                variant="outlined"
                                                                sx={{
                                                                    borderColor: count > 0 ? 'success.main' : 'divider',
                                                                    color: count > 0 ? 'success.main' : 'text.secondary',
                                                                    '& .MuiChip-icon': {
                                                                        fontSize: 16,
                                                                    },
                                                                }}
                                                            />

                                                            <Box sx={{ display: 'flex', alignItems: 'center', color: 'primary.main' }}>
                                                                <Typography variant="caption" sx={{ fontWeight: 500, mr: 0.5 }}>
                                                                    {language === "en" ? "Manage" : "प्रबंधित करें"}
                                                                </Typography>
                                                                <ChevronRightIcon fontSize="small" />
                                                            </Box>
                                                        </Box>
                                                    </Stack>
                                                </CardContent>
                                            </CardActionArea>
                                        </Card>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    )}
                    {!loading && (
                        <Paper
                            sx={{
                                mt: 4,
                                p: 3,
                                backgroundColor: 'background.default',
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 2,
                            }}
                        >
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} md={8}>
                                    <Stack spacing={1}>
                                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                            {language === "en" ? "Gallery Summary" : "गैलरी सारांश"}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {language === "en"
                                                ? "Upload high-quality photos for each category to showcase your venue effectively."
                                                : "अपने वेन्यू को प्रभावी ढंग से प्रदर्शित करने के लिए प्रत्येक श्रेणी के लिए उच्च-गुणवत्ता वाली तस्वीरें अपलोड करें।"}
                                        </Typography>
                                    </Stack>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Paper
                                        variant="outlined"
                                        sx={{
                                            p: 2,
                                            backgroundColor: 'primary.50',
                                            borderColor: 'primary.100',
                                        }}
                                    >
                                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                                            <Box>
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    {language === "en" ? "Total Photos" : "कुल तस्वीरें"}
                                                </Typography>
                                                <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>
                                                    {Object.values(imageCounts).reduce((a, b) => a + b, 0)}
                                                </Typography>
                                            </Box>
                                            <CollectionsIcon sx={{ color: 'primary.main', fontSize: 32 }} />
                                        </Stack>
                                    </Paper>
                                </Grid>
                            </Grid>
                        </Paper>
                    )}
                    {/* </Container> */}
                </div>
            </div>
        </div>
    );
};

export default VenuePreviousWork;